/**
 * NarrowcastingEditor - Slide editor with drag & drop, transitions, and render
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    horizontalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    PlayArrow as RenderIcon,
    Save as SaveIcon,
    Download as DownloadIcon,
    Tv as NarrowcastingIcon,
    Movie as MovieIcon,
    MusicNote as MusicNoteIcon,
    Wallpaper as WallpaperIcon,
} from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import toast from 'react-hot-toast';
import SlideCard from './components/SlideCard';
import TransitionPicker from './components/TransitionPicker';
import RenderProgress from './components/RenderProgress';

// Temporary IDs for new slides that haven't been saved yet
let tempIdCounter = 0;
const newTempId = () => `temp_${++tempIdCounter}_${Date.now()}`;

export default function NarrowcastingEditor({ presentationId, onBack, defaultBgId, defaultMuziekId }) {
    const [presentation, setPresentation] = useState(null);
    const [slides, setSlides] = useState([]);
    const [slideConfigs, setSlideConfigs] = useState([]); // Slide configuraties
    const [bgConfigs, setBgConfigs] = useState([]); // Background configuraties
    const [muziekConfigs, setMuziekConfigs] = useState([]); // Muziek configuraties
    const [selectedBg, setSelectedBg] = useState(defaultBgId || null);
    const [selectedMuziek, setSelectedMuziek] = useState(defaultMuziekId || null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [activeId, setActiveId] = useState(null);

    // Render state
    const [isRendering, setIsRendering] = useState(false);
    const [renderStatus, setRenderStatus] = useState(null);
    const pollingRef = useRef(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        })
    );

    // Load presentation
    useEffect(() => {
        loadPresentation();
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [presentationId]);

    // Ctrl+Z / Cmd+Z buiten tekstvelden = laatste actie terugdraaien
    useEffect(() => registreerOngedaanSneltoets(), []);

    // Start polling if already rendering
    useEffect(() => {
        if (presentation && (presentation.status === 'rendering' || presentation.status === 'concatenating')) {
            startPolling();
        }
    }, [presentation?.status]);

    const loadPresentation = async () => {
        setLoading(true);
        try {
            const res = await authFetch(`${BACKEND_URL}/Api/narrowcasting/presentations/${presentationId}`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Laden mislukt');

            setPresentation(data);
            setSlides(data.slides || []);
            setHasUnsavedChanges(false);

            if (data.status === 'rendering' || data.status === 'concatenating') {
                setIsRendering(true);
            }

            // Fetch slide configurations from huisstijl element
            if (data.huisstijl_element_id) {
                try {
                    const cfgRes = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/pg_${data.huisstijl_element_id}/configuraties`);
                    const cfgData = await cfgRes.json();
                    if (cfgRes.ok) {
                        const allConfigs = (cfgData.configuraties || []).map(c => ({
                            id: c.id,  // pg_123 format
                            numId: typeof c.id === 'string' && c.id.startsWith('pg_') ? parseInt(c.id.slice(3), 10) : c.id,
                            type: c.fields?.Type,
                            formaat: c.fields?.Formaat,
                            velden: c.fields?.Velden || [],
                            compositienaam: c.fields?.Composititenaam || '',
                            label: c.fields?.Label || '',
                        }));
                        setSlideConfigs(allConfigs.filter(c => c.type !== 'Background' && c.type !== 'Muziek'));
                        setBgConfigs(allConfigs.filter(c => c.type === 'Background'));
                        setMuziekConfigs(allConfigs.filter(c => c.type === 'Muziek'));
                    }
                } catch (e) {
                    console.warn('Could not load slide configs:', e.message);
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Save slides to AI Engine
    const saveSlides = useCallback(async () => {
        setSaving(true);
        try {
            const slidesPayload = slides.map((s, i) => ({
                id: typeof s.id === 'number' ? s.id : undefined,
                sort_order: i,
                slide_type: s.slide_type,
                titel: s.titel || null,
                subtitel: s.subtitel || null,
                foto_urls: s.foto_urls || null,
                duration: s.duration || 6,
                transition_type: s.transition_type || 'fade',
                transition_duration: s.transition_duration ?? 0.5,
                // Narrowcasting-specific fields
                slide_num: s.slide_num,
                config_id: s.config_id,
                layers: s.layers,
                text_values: s.text_values,
            }));

            const res = await authFetch(`${BACKEND_URL}/Api/narrowcasting/presentations/${presentationId}/slides`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(slidesPayload),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Opslaan mislukt');

            setSlides(Array.isArray(data) ? data : []);
            setHasUnsavedChanges(false);
            toast.success('Opgeslagen');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    }, [slides, presentationId]);

    // Slide operations
    const handleUpdateSlide = (slideId, updates) => {
        setSlides(prev => prev.map(s => s.id === slideId ? { ...s, ...updates } : s));
        setHasUnsavedChanges(true);
    };

    const handleAddSlide = () => {
        // Pick first content config as default, or fallback
        const contentConfigs = slideConfigs.filter(c => c.type && !c.type.includes('Begin') && !c.type.includes('Eind'));
        const defaultConfig = contentConfigs[0] || null;

        const newSlide = {
            id: newTempId(),
            sort_order: slides.length,
            slide_type: 'content',
            config_id: defaultConfig?.id || null,
            layers: defaultConfig?.velden || ['Titel_1', 'Subtitel_1', 'Foto_1'],
            text_values: {},
            foto_urls: [],
            duration: 6,
            transition_type: 'fade',
            transition_duration: 0.5,
            render_status: 'pending',
        };
        // Insert before eind slide if one exists, otherwise append
        setSlides(prev => {
            const eindIndex = prev.findIndex(s => s.slide_type === 'eind');
            if (eindIndex >= 0) {
                const copy = [...prev];
                copy.splice(eindIndex, 0, newSlide);
                return copy.map((s, i) => ({ ...s, sort_order: i }));
            }
            return [...prev, newSlide];
        });
        setHasUnsavedChanges(true);
    };

    const handleDeleteSlide = (slideId) => {
        const index = slides.findIndex(s => s.id === slideId);
        const verwijderd = slides[index];
        setSlides(prev => prev.filter(s => s.id !== slideId));
        setHasUnsavedChanges(true);
        if (verwijderd) {
            // Ongedaan maken = slide op dezelfde plek terugzetten (lokaal, tot Opslaan)
            biedOngedaanAan('Slide verwijderd', async () => {
                setSlides(prev => {
                    if (prev.some(s => s.id === slideId)) return prev;
                    const copy = [...prev];
                    copy.splice(Math.min(index, copy.length), 0, verwijderd);
                    return copy.map((s, i) => ({ ...s, sort_order: i }));
                });
                setHasUnsavedChanges(true);
            });
        }
    };

    const handleUploadFoto = async (slideId, file) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            // Need to save first if slide has temp ID
            const slide = slides.find(s => s.id === slideId);
            if (!slide || typeof slide.id === 'string') {
                toast.error('Sla eerst de slides op voordat je foto\'s uploadt');
                return;
            }

            const res = await authFetch(
                `${BACKEND_URL}/Api/narrowcasting/presentations/${presentationId}/slides/${slideId}/upload-foto`,
                { method: 'POST', body: formData }
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Upload mislukt');

            // Update local state
            setSlides(prev => prev.map(s =>
                s.id === slideId ? { ...s, foto_urls: data.foto_urls } : s
            ));
            toast.success('Foto geüpload');
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDeleteFoto = async (slideId, index) => {
        if (!(await bevestig({ titel: 'Foto verwijderen?', tekst: 'De foto wordt direct van de server verwijderd. Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
        try {
            const res = await authFetch(
                `${BACKEND_URL}/Api/narrowcasting/presentations/${presentationId}/slides/${slideId}/foto/${index}`,
                { method: 'DELETE' }
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Verwijderen mislukt');

            setSlides(prev => prev.map(s =>
                s.id === slideId ? { ...s, foto_urls: data.foto_urls } : s
            ));
        } catch (err) {
            toast.error(err.message);
        }
    };

    // Drag & drop
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        setActiveId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setSlides(prev => {
            const oldIndex = prev.findIndex(s => s.id === active.id);
            const newIndex = prev.findIndex(s => s.id === over.id);
            const newSlides = arrayMove(prev, oldIndex, newIndex);
            return newSlides.map((s, i) => ({ ...s, sort_order: i }));
        });
        setHasUnsavedChanges(true);
    };

    // Render
    const handleStartRender = async () => {
        // Save first if needed
        if (hasUnsavedChanges) {
            await saveSlides();
        }

        try {
            const res = await authFetch(`${BACKEND_URL}/Api/narrowcasting/presentations/${presentationId}/render`, {
                method: 'POST',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Render starten mislukt');

            toast.success(`${data.jobs.length} slide render jobs aangemaakt`);
            setIsRendering(true);
            startPolling();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const startPolling = () => {
        if (pollingRef.current) clearInterval(pollingRef.current);

        const poll = async () => {
            try {
                const res = await authFetch(
                    `${BACKEND_URL}/Api/narrowcasting/presentations/${presentationId}/render-status`
                );
                const data = await res.json();
                if (!res.ok) return;

                setRenderStatus(data);

                // Stop polling when done
                if (data.status === 'completed' || data.status === 'error' ||
                    (data.anyFailed && data.allSlidesCompleted)) {
                    if (pollingRef.current) clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    setIsRendering(false);

                    if (data.status === 'completed') {
                        toast.success('Video klaar!');
                    } else if (data.anyFailed) {
                        toast.error('Een of meer slides zijn mislukt');
                    }
                }
            } catch (err) {
                // Silent
            }
        };

        poll(); // Poll immediately
        pollingRef.current = setInterval(poll, 3000);
    };

    const handleDownload = async () => {
        if (!renderStatus) return;

        // Find the concat job ID to download
        // The output should be downloadable via the render status
        const concatJobId = renderStatus.concatJobId;
        if (concatJobId) {
            window.open(`${BACKEND_URL}/Api/narrowcasting/download/${concatJobId}`, '_blank');
        } else if (renderStatus.outputUrl) {
            window.open(renderStatus.outputUrl, '_blank');
        } else {
            toast.error('Download nog niet beschikbaar');
        }
    };

    const handleOutputFormatChange = async (format) => {
        try {
            const res = await authFetch(`${BACKEND_URL}/Api/narrowcasting/presentations/${presentationId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ output_format: format }),
            });
            if (!res.ok) throw new Error('Formaat wijzigen mislukt');
            setPresentation(prev => ({ ...prev, output_format: format }));
        } catch (err) {
            toast.error(err.message);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
                <Alert severity="error">{error}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mt: 2 }}>
                    Terug
                </Button>
            </Box>
        );
    }

    const activeSlide = slides.find(s => s.id === activeId);

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={onBack}>
                    <ArrowBackIcon />
                </IconButton>
                <NarrowcastingIcon sx={{ fontSize: 28, color: '#6366f1' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                    {presentation?.naam || 'Presentatie'}
                </Typography>

                {/* Output format toggle */}
                <ToggleButtonGroup
                    value={presentation?.output_format || 'mp4'}
                    exclusive
                    onChange={(e, val) => { if (val) handleOutputFormatChange(val); }}
                    size="small"
                >
                    <ToggleButton value="mp4" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                        .mp4
                    </ToggleButton>
                    <ToggleButton value="mov" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                        .mov
                    </ToggleButton>
                </ToggleButtonGroup>

                {hasUnsavedChanges && (
                    <Chip label="Niet opgeslagen" size="small" sx={{ bgcolor: '#fef3c7', color: '#d97706' }} />
                )}

                <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={saveSlides}
                    disabled={saving || !hasUnsavedChanges}
                    sx={{ textTransform: 'none' }}
                >
                    {saving ? 'Opslaan...' : 'Opslaan'}
                </Button>

                <Button
                    variant="contained"
                    startIcon={<RenderIcon />}
                    onClick={handleStartRender}
                    disabled={isRendering || slides.length === 0}
                    sx={{
                        bgcolor: '#6366f1',
                        '&:hover': { bgcolor: '#4f46e5' },
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    {isRendering ? 'Bezig...' : 'Renderen'}
                </Button>
            </Box>

            {/* Render progress */}
            {renderStatus && (
                <RenderProgress
                    renderStatus={renderStatus}
                    onDownload={handleDownload}
                    onClose={() => setRenderStatus(null)}
                />
            )}

            {/* Background + Muziek selectors */}
            {(bgConfigs.length > 0 || muziekConfigs.length > 0) && (
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {bgConfigs.length > 0 && (
                        <>
                            <WallpaperIcon sx={{ color: '#f59e0b' }} />
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel sx={{ fontSize: '0.85rem' }}>Background</InputLabel>
                                <Select
                                    value={selectedBg || ''}
                                    onChange={(e) => setSelectedBg(e.target.value || null)}
                                    label="Background"
                                    sx={{ fontSize: '0.85rem' }}
                                >
                                    <MenuItem value=""><em>Geen background</em></MenuItem>
                                    {bgConfigs.map(cfg => (
                                        <MenuItem key={cfg.id} value={cfg.id}>
                                            {cfg.label || cfg.compositienaam || 'Background'}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}
                    {muziekConfigs.length > 0 && (
                        <>
                            <MusicNoteIcon sx={{ color: '#ec4899' }} />
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel sx={{ fontSize: '0.85rem' }}>Muziek</InputLabel>
                                <Select
                                    value={selectedMuziek || ''}
                                    onChange={(e) => setSelectedMuziek(e.target.value || null)}
                                    label="Muziek"
                                    sx={{ fontSize: '0.85rem' }}
                                >
                                    <MenuItem value=""><em>Geen muziek</em></MenuItem>
                                    {muziekConfigs.map(cfg => (
                                        <MenuItem key={cfg.id} value={cfg.id}>
                                            {cfg.label || 'Muziek'}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}
                </Paper>
            )}

            {/* Slide editor */}
            <Paper sx={{ p: 3, borderRadius: 2, overflow: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Slides ({slides.length})
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddSlide}
                        sx={{ textTransform: 'none' }}
                    >
                        Slide toevoegen
                    </Button>
                </Box>

                {slides.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Voeg slides toe om te beginnen.
                    </Alert>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={slides.map(s => s.id)}
                            strategy={horizontalListSortingStrategy}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 0,
                                    overflowX: 'auto',
                                    pb: 2,
                                    minHeight: 400,
                                }}
                            >
                                {slides.map((slide, i) => (
                                    <React.Fragment key={slide.id}>
                                        <SlideCard
                                            slide={slide}
                                            slideConfigs={slideConfigs}
                                            onUpdate={handleUpdateSlide}
                                            onDelete={handleDeleteSlide}
                                            onUploadFoto={handleUploadFoto}
                                            onDeleteFoto={handleDeleteFoto}
                                        />
                                        {i < slides.length - 1 && (
                                            <TransitionPicker
                                                slide={slides[i + 1]}
                                                onUpdate={handleUpdateSlide}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </Box>
                        </SortableContext>

                        <DragOverlay>
                            {activeSlide && (
                                <SlideCard
                                    slide={activeSlide}
                                    onUpdate={() => {}}
                                    onDelete={() => {}}
                                    onUploadFoto={() => {}}
                                    onDeleteFoto={() => {}}
                                    overlay
                                />
                            )}
                        </DragOverlay>
                    </DndContext>
                )}
            </Paper>
        </Box>
    );
}
